/**
 * Employee Master — detail / edit view for ACTIVE workers.
 *
 * Route: /hr/employees/:id
 *
 * Reuses every tab component from the Joining workflow (BasicDetails,
 * Address, Employment, Attendance, Salary, Statutory, Bank, Nominee,
 * Skill, Safety, Documents, System Access). Both pages PATCH the same
 * /joining/{id} endpoint backed by the same `employees` row — so any
 * field edited here is immediately visible in the Joining detail and
 * vice versa.
 *
 * The difference vs JoiningDetailPage:
 *   - No lifecycle stepper (this view is for already-joined workers)
 *   - No "Activate Employee" button
 *   - No checklist / audit-log tabs (kept clean for daily HR use,
 *     accessible via Joining detail if needed)
 *   - "Back" goes to /hr/employees instead of /hr/joining
 */
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, Row, Space, Spin, Tabs, Tag, Tooltip,
  Typography,
} from 'antd'
import {
  ArrowLeftOutlined, EditOutlined, HistoryOutlined, SafetyOutlined,
} from '@ant-design/icons'

import { joiningApi, type JoiningStatus } from '@/features/hr/joiningApi'
import { displayEmpCode, isDraftEmpCode } from '@/shared/utils/empCode'
import PhotoUploadCard from './joining/PhotoUploadCard'
import BasicDetailsTab from './joining/BasicDetailsTab'
import AddressTab from './joining/AddressTab'
import EmploymentTab from './joining/EmploymentTab'
import AttendanceTab from './joining/AttendanceTab'
import SalaryTab from './joining/SalaryTab'
import StatutoryTab from './joining/StatutoryTab'
import BankTab from './joining/BankTab'
import NomineeTab from './joining/NomineeTab'
import SkillTab from './joining/SkillTab'
import SafetyTab from './joining/SafetyTab'
import DocumentsTab from './joining/DocumentsTab'
import SystemAccessTab from './joining/SystemAccessTab'
import AuditLogTab from './joining/AuditLogTab'

const { Text } = Typography

const STATUS_COLOR: Record<JoiningStatus, string> = {
  DRAFT: 'default',
  DOCUMENT_PENDING: 'gold',
  DOCUMENT_RECEIVED: 'cyan',
  VERIFICATION_PENDING: 'orange',
  VERIFIED: 'lime',
  EMP_CODE_GENERATED: 'blue',
  PAYROLL_PENDING: 'geekblue',
  STATUTORY_PENDING: 'purple',
  BIOMETRIC_PENDING: 'magenta',
  INDUCTION_PENDING: 'volcano',
  APPROVAL_PENDING: 'gold',
  ACTIVE: 'green',
  HOLD: 'default',
  REJECTED: 'red',
}

export default function EmployeeDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const detailQuery = useQuery({
    queryKey: ['employee-detail', id],
    // Same endpoint as JoiningDetailPage — returns the full employee
    // + joining sub-records (documents, nominees, etc.).
    queryFn: () => joiningApi.detail(id),
    enabled: !!id,
  })

  if (detailQuery.isLoading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>
  }
  if (!detailQuery.data) {
    return <Card>Employee not found.</Card>
  }
  const detail = detailQuery.data
  const isActive = detail.joining_status === 'ACTIVE'
  // detail.employee is typed as Record<string,unknown> on the API; safe
  // narrow-cast for the few header fields we display.
  const emp = detail.employee as {
    biometric_id?: string | null
    department_name?: string | null
    designation?: string | null
  }

  const tabItems = [
    { key: 'basic', label: 'Basic Details', children: <BasicDetailsTab detail={detail} /> },
    { key: 'address', label: 'Address', children: <AddressTab detail={detail} /> },
    { key: 'employment', label: 'Employment', children: <EmploymentTab detail={detail} /> },
    { key: 'attendance', label: 'Attendance', children: <AttendanceTab detail={detail} /> },
    { key: 'salary', label: 'Salary', children: <SalaryTab detail={detail} /> },
    { key: 'statutory', label: 'Statutory', children: <StatutoryTab detail={detail} /> },
    { key: 'bank', label: 'Bank', children: <BankTab detail={detail} /> },
    { key: 'nominee', label: 'Nominee', children: <NomineeTab detail={detail} /> },
    { key: 'skill', label: 'Skill', children: <SkillTab detail={detail} /> },
    { key: 'safety', label: 'Safety', children: <SafetyTab detail={detail} /> },
    { key: 'documents', label: 'Documents', children: <DocumentsTab detail={detail} /> },
    { key: 'system', label: 'System Access', children: <SystemAccessTab detail={detail} /> },
    { key: 'audit', label: <Space size={4}><HistoryOutlined />Audit Log</Space>,
      children: <AuditLogTab detail={detail} /> },
  ]

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/employees')}>
          Back to Employees
        </Button>
        {!isActive && (
          <Tooltip title="This worker hasn't completed the joining workflow yet.">
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/hr/joining/${id}`)}
            >
              Open in Joining workflow
            </Button>
          </Tooltip>
        )}
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Space wrap>
                <h2 style={{ margin: 0 }}>{detail.full_name}</h2>
                <Tag color={STATUS_COLOR[detail.joining_status]}>
                  {detail.joining_status.replace(/_/g, ' ')}
                </Tag>
                {detail.emp_code && (
                  <Tag color={isDraftEmpCode(detail.emp_code) ? 'default' : 'blue'}>
                    {displayEmpCode(detail.emp_code)}
                  </Tag>
                )}
                {emp.biometric_id && <Tag>BIO {emp.biometric_id}</Tag>}
                {emp.department_name && (
                  <Tag color="cyan">{emp.department_name}</Tag>
                )}
                {emp.designation && <Tag>{emp.designation}</Tag>}
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Edit any field on the tabs below. Changes save to the same
                record used by Payroll, Attendance, and Worker Allocation.
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {!isActive && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          icon={<SafetyOutlined />}
          message="This worker is not yet ACTIVE"
          description={
            <>
              Status: <Tag color={STATUS_COLOR[detail.joining_status]}>
                {detail.joining_status.replace(/_/g, ' ')}
              </Tag>
              Editing here is allowed, but the joining workflow checklist
              and activation flow live on the Joining detail page.
            </>
          }
        />
      )}

      <Row gutter={16}>
        <Col xs={24} md={6}>
          <PhotoUploadCard detail={detail} />
        </Col>
        <Col xs={24} md={18}>
          <Card size="small">
            <Tabs items={tabItems} defaultActiveKey="employment" />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
